import { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Box,
  IconButton,
  Tooltip,
  Modal,
  Divider,
} from "@mui/material";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { useSession } from "providers/SessionProvider";
import useCrud from "hooks/useCrud";
import { queryBuilder } from "helpers/query";
import { HISTORY_GROUPED_BY_LOTE_URL } from "constants/urls";
import { Client, HistoryFiltersInterface, IMovementsQuery, Location } from "types";
import CustomGrid from "components/lists/CustomGrid";
import { FaFileDownload } from "react-icons/fa";
import { buildExcelHeader, exportToExcel } from "helpers/exportToExcel";
import HistoryFilters from "./HistoryFilters";
import { formatDateOnlyDMY } from "helpers";
import {
  getMovementTypeLabel,
  formatGroupedMovementHistoryUnitCounts,
  isEntryMovement,
  isExitMovement,
} from "helpers/movement";
import { MdVisibility } from "react-icons/md";
import { MovementTypes } from "constants/index";

interface HistoryListProps {
  viewMode: "detalle" | "resumido";
}

export default function HistoryList({ viewMode }: HistoryListProps) {
  const {
    data: history,
    fetchData: getMovementHistory,
    loading: historyLoading,
  } = useCrud<any>(HISTORY_GROUPED_BY_LOTE_URL);

  const { stores, clients, locations, articles, userData } = useSession();

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    [],
  );
  const [filters, setFilters] = useState<HistoryFiltersInterface>({
    storeIds: [],
    locationId: undefined,
    clientIds: [],
    articleId: undefined,
    articleDescription: "",
    lote: "",
    oc: "",
    amount_per_container: "",
    fecha_vencimiento: "",
    fecha_ingreso: "",
    fecha_desde: null as Date | null,
    fecha_hasta: null as Date | null,
  });
  const [selectedClients, setSelectedClients] = useState<Client[]>([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);

  const uniqueLotes = useMemo(
    () =>
      Array.from(
        new Set(
          history.map((h) => h.lote).filter((l): l is string => Boolean(l)),
        ),
      ),
    [history],
  );

  const scopedClientIds = useMemo(() => {
    const parentIds = selectedClients.map((client) => client.id);

    return [
      ...parentIds,
      ...clients
        .filter((client) => parentIds.includes(client.parent_client_id ?? 0))
        .map((client) => client.id),
    ];
  }, [clients, selectedClients]);

  const uniqueArticles = useMemo(() => {
    const scopedArticles = scopedClientIds.length
      ? articles.filter((article) => scopedClientIds.includes(article.client_id))
      : articles;

    return Array.from(new Map(scopedArticles.map((a) => [a.id, a])).values());
  }, [articles, scopedClientIds]);

  const filteredArticles = useMemo(() => {
    if (!filters.locationId) return uniqueArticles;

    const articlesFromHistory = history
      .filter((h) =>
        h.locations?.some((l: any) => l.location?.id === filters.locationId),
      )
      .map((h) => h.article)
      .filter(Boolean);

    return Array.from(
      new Map(articlesFromHistory.map((a: any) => [a.id, a])).values(),
    );
  }, [history, filters.locationId, uniqueArticles]);

  const availableLocationsForArticle = useMemo(() => {
    if (!filters.articleId) return locations;

    const locIds = history
      .filter((h) => h.article?.id === filters.articleId)
      .flatMap((h) => h.locations?.map((l: any) => l.location?.id) ?? []);

    const locSet = new Set(locIds);

    return locations.filter((l) => locSet.has(l.id));
  }, [history, filters.articleId, locations]);

  const availableStoresForArticle = useMemo(() => {
    if (!filters.articleId) return stores;

    const storeIds = history
      .filter((h) => h.article?.id === filters.articleId)
      .flatMap((h) => h.locations?.map((l: { location?: Location }) => l.location?.store_id) ?? []);
    const storeSet = new Set(storeIds);

    return stores.filter((s) => storeSet.has(s.id));
  }, [history, filters.articleId, stores]);

  const uniqueFechasMovimiento = useMemo(
    () =>
      Array.from(
        new Set(
          history.map((h) => h.movement?.date?.split("T")[0]).filter(Boolean),
        ),
      ),
    [history],
  );

  const handleSearch = async () => {
    const query = queryBuilder({
      storeIds: filters.storeIds,
      locationId: filters.locationId,
      clientIds: filters.clientIds,
      articleId: filters.articleId,
      lote: filters.lote || undefined,
      oc: filters.oc || undefined,
      amount_per_container: filters.amount_per_container || undefined,
      articleDescription: filters.articleDescription.trim() || undefined,
      fecha_vencimiento: filters.fecha_vencimiento || undefined,
      fecha_ingreso: filters.fecha_ingreso || undefined,
      fecha_desde: filters.fecha_desde
        ? filters.fecha_desde.toISOString()
        : undefined,
      fecha_hasta: filters.fecha_hasta
        ? filters.fecha_hasta.toISOString()
        : undefined,
    } as IMovementsQuery);

    await getMovementHistory(query, true);
  };

  useEffect(() => {
    handleSearch();
  }, [filters]);

  const handleExportToExcel = () => {
    const { headerRows } = buildExcelHeader(clients, filters, userData);

    const columns: Record<string, (a: any) => any> = {
      "Nº Movimiento": (a) => a.movement?.id ?? "-",

      "Tipo de Movimiento": (a) =>
        getMovementTypeLabel(a.movement?.movement_type_id, {
          isTransferFrom: a.isTransferFrom,
          isTransferTo: a.isTransferTo,
        }),

      "Fecha de Movimiento": (a) =>
        a.movement?.date ? formatDateOnlyDMY(a.movement.date) : "-",

      "Código de Artículo": (a) => a.article?.code ?? "-",

      Descripción: (a) => a.article?.description ?? "-",

      Cliente: (a) => a.article?.client?.code ?? "-",

      "Doc. del Cliente": (a) => a.movement?.client_document || "-",

      "Cliente Asociado": (a) => a.movement?.associated_client || "-",

      Ubicación: (a) =>
        a.locations?.map((l: any) => l.location?.code).filter(Boolean).join(", ") || "-",
    };

    if (filterVisibility.show_lote) {
      columns["Lote"] = (a) => a.lote ?? "-";
    }

    columns["Fardo/Paquete"] = (a) =>
      formatGroupedMovementHistoryUnitCounts(a.movement?.movement_type_id, a.locations);

    columns["Entrada"] = (a) =>
      isEntryMovement(a.movement?.movement_type_id) ? a.totalAmount : "-";

    columns["Salida"] = (a) =>
      isExitMovement(a.movement?.movement_type_id) ? a.totalAmount : "-";

    columns["Saldo"] = (a) => a.saldo ?? "-";

    exportToExcel({
      data: history,
      columns,
      sheetName: "Historial",
      fileNamePrefix: "Historial_Movimientos",
      headerRows,
    });
  };

  const filterVisibility = useMemo(() => {
    if (selectedClients.length === 0) {
      return {
        show_lote: true,
        show_oc: true,
        show_fecha_vencimiento: true,
      };
    }

    return {
      show_lote: selectedClients.some((c) => c.show_lote),
      show_oc: selectedClients.some((c) => c.show_oc),
      show_fecha_vencimiento: selectedClients.some(
        (c) => c.show_fecha_vencimiento,
      ),
    };
  }, [selectedClients]);

  const structure = useMemo(
    () => ({
      name: "movementHistoryGrouped",
      labelName: "Historial de Movimientos",
      fields: [
        {
          field: "movement_id",
          headerName: "Nº Movimiento",
          flex: 1,
          valueGetter: (p: any) => p.row.movement?.id ?? "-",
        },
        {
          field: "tipo_movimiento",
          headerName: "Tipo de Movimiento",
          flex: 1,
          valueGetter: (p: any) =>
            getMovementTypeLabel(p.row.movement?.movement_type_id),
        },
        {
          field: "fecha",
          headerName: "Fecha de Movimiento",
          flex: 1,
          valueGetter: (p: any) =>
            p.row.movement?.date
              ? new Date(p.row.movement.sort_date ?? p.row.movement.date)
              : null,
          valueFormatter: (params: any) =>
            params.value ? formatDateOnlyDMY(params.value.toISOString()) : "-",
        },
        {
          field: "codigo",
          headerName: "Código de Artículo",
          flex: 1,
          valueGetter: (p: any) => p.row.article?.code ?? "-",
        },
        {
          field: "descripcion",
          headerName: "Descripción",
          flex: 1.5,
          valueGetter: (p: any) => p.row.article?.description ?? "-",
        },
        {
          field: "cliente",
          headerName: "Cliente",
          flex: 1,
          valueGetter: (p: any) => p.row.article?.client?.code ?? "-",
        },
        {
          field: "client_document",
          headerName: "Doc. del cliente",
          flex: 1,
          valueGetter: (p: any) => p.row.movement?.client_document || "-",
        },
        {
          field: "associated_client",
          headerName: "Cliente Asociado",
          flex: 1.2,
          valueGetter: (p: any) => p.row.movement?.associated_client || "-",
        },
        {
          field: "lote",
          headerName: "Lote",
          flex: 1,
          valueGetter: (p: any) => p.row.lote ?? "-",
        },
        {
          field: "fardo_paquete",
          headerName: "Fardo/Paquete",
          flex: 1.4,
          valueGetter: (p: any) =>
            formatGroupedMovementHistoryUnitCounts(
              p.row.movement?.movement_type_id,
              p.row.locations,
            ),
        },
        {
          field: "entrada",
          headerName: "Entrada",
          flex: 0.7,
          valueGetter: (p: any) =>
            isEntryMovement(p.row.movement?.movement_type_id)
              ? p.row.totalAmount
              : "-",
        },
        {
          field: "salida",
          headerName: "Salida",
          flex: 0.7,
          valueGetter: (p: any) =>
            isExitMovement(p.row.movement?.movement_type_id)
              ? p.row.totalAmount
              : "-",
        },
        {
          field: "saldo",
          headerName: "Saldo",
          flex: 0.8,
          type: "number",
          valueGetter: (p: any) => p.row.saldo ?? null,
          valueFormatter: (params: any) =>
            params.value != null ? params.value : "-",
        },
        {
          field: "actions",
          headerName: "",
          width: 60,
          sortable: false,
          filterable: false,
          align: "center",
          headerAlign: "center",
          renderCell: (params: any) => (
            <Tooltip title="Ver detalle por ubicación">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRow(params.row);
                  setDetailOpen(true);
                }}
              >
                <MdVisibility fontSize="small" />
              </IconButton>
            </Tooltip>
          ),
        },
      ] as GridColDef[],
    }),
    [filterVisibility],
  );

  useEffect(() => {
    setFilters({
      storeIds: [],
      locationId: undefined,
      clientIds: [],
      articleId: undefined,
      lote: "",
      oc: "",
      amount_per_container: "",
      articleDescription: "",
      fecha_vencimiento: "",
      fecha_ingreso: "",
      fecha_desde: null as Date | null,
      fecha_hasta: null as Date | null,
    });

    setSelectedClients([]);
  }, [viewMode]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper elevation={4} sx={{ p: 2 }}>
          <Typography variant="h6">Filtros</Typography>
          <HistoryFilters
            key={viewMode}
            viewMode={viewMode}
            filters={filters}
            setFilters={setFilters}
            selectedClients={selectedClients}
            setSelectedClients={setSelectedClients}
            uniqueValues={{
              lotes: uniqueLotes,
              movimientoFechas: uniqueFechasMovimiento,
            }}
            filteredArticles={filteredArticles}
            availableStores={availableStoresForArticle}
            availableLocations={availableLocationsForArticle}
            filterVisibility={filterVisibility}
            clients={clients}
            stores={stores}
            uniqueArticles={uniqueArticles}
            loading={historyLoading}
          />

          <Grid container justifyContent="flex-end" mb={2}>
            <Button
              variant="contained"
              color="success"
              startIcon={<FaFileDownload />}
              onClick={handleExportToExcel}
              disabled={history.length === 0}
            >
              Exportar Excel
            </Button>
          </Grid>

          {historyLoading ? (
            <Grid item xs={12} display="flex" justifyContent="center" my={6}>
              <CircularProgress />
            </Grid>
          ) : (
            <CustomGrid<any>
              getRowId={(row) =>
                `${row.movement.id}-${row.article.id}-${row.lote}`
              }
              structure={structure}
              rows={history}
              onEdit={() => {}}
              selectionModel={selectionModel}
              onRowSelection={setSelectionModel}
              checkboxSelection={false}
              initialState={{
                sorting: {
                  sortModel: [{ field: "fecha", sort: "desc" }],
                },
              }}
            />
          )}

          {!historyLoading && history.length === 0 && (
            <Alert severity="info">
              <Typography variant="body2">
                No hay resultados disponibles
              </Typography>
            </Alert>
          )}

          <Modal
            open={detailOpen}
            onClose={() => setDetailOpen(false)}
            aria-labelledby="movement-detail-title"
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 700,
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 3,
                borderRadius: 2,
                maxHeight: "80vh",
                overflowY: "auto",
              }}
            >
              {selectedRow && (
                <>
                  <Typography id="movement-detail-title" variant="h6" mb={1}>
                    Detalle de Movimiento
                  </Typography>

                  <Typography variant="body2">
                    <strong>Artículo:</strong> {selectedRow.article.code} –{" "}
                    {selectedRow.article.description}
                  </Typography>

                  <Typography variant="body2">
                    <strong>Lote:</strong> {selectedRow.lote}
                  </Typography>

                  <Typography variant="body2" mb={2}>
                    <strong>Saldo del lote:</strong> {selectedRow.saldo}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle1" gutterBottom>
                    Ubicaciones utilizadas
                  </Typography>

                  {selectedRow.locations?.length ? (
                    <Grid container spacing={1}>
                      {selectedRow.locations.map((l: any) => (
                        <Grid item xs={12} key={l.location.id}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Box>
                              <Typography variant="body2">
                                <strong>Ubicación:</strong> {l.location.code}
                              </Typography>
                            </Box>

                            <Box textAlign="right">
                              <Typography variant="body2">
                                <strong>Cantidad:</strong> {l.amount}
                              </Typography>
                              {selectedRow.movement?.movement_type_id === MovementTypes.WorkOrder && (
                                <>
                                  <Typography variant="body2">
                                    <strong>Unidades en fardo:</strong> {l.bundles ?? "–"}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Unidades en paquete:</strong> {l.packages ?? "–"}
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2">
                      No hay ubicaciones asociadas
                    </Typography>
                  )}

                  <Box mt={3} display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      onClick={() => setDetailOpen(false)}
                    >
                      Cerrar
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Modal>
        </Paper>
      </Grid>
    </Grid>
  );
}
